/**
 * computeProjections.js — Smart project projection engine.
 *
 * Computes projections using per-annotator velocity instead of flat team averages.
 * Each annotator's hours/week and annotations/hour are computed individually,
 * then summed to get real team output. This handles:
 *   - Varying schedules (40h/week vs 5h/week)
 *   - New hires (< 1 week of data → uses capacity estimate)
 *   - Offboarded members (excluded automatically via status filter)
 *   - Quality-adjusted output (rejections increase effective remaining work)
 *
 * All functions are pure — no DB calls, no side effects.
 */

import { resolveAssignmentRate } from './projectMetrics';

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
// R23 LOW-2 audit fix: removed dead `MS_PER_DAY` constant — leftover from
// R6's removal of `computeSmartProjections` (commit a55ab57). No current
// reference; trivial to re-derive if ever needed.
const MIN_WEEKS_FOR_STABLE_DATA = 1;
// Floor for weeksActive to avoid division-by-zero at hour-zero of an
// assignment. 0.14 ≈ 1/7 = 1 day in weeks. Phase 11 slot 1 (Invariant #16).
const MIN_WEEKS_ACTIVE_FLOOR = 1 / 7;

/**
 * Compute per-annotator velocity from assignment data.
 *
 * @param {Array} assignments - assignment rows with individual join
 * @param {number} defaultHoursPerWeek - fallback for new hires (from job.hours_per_week_max)
 * @param {Date|number} [now] - injected wall-clock (Date or ms-since-epoch).
 *   Phase 2 cross-phase sweep / R-cross-1 audit fix: previously read a
 *   fresh `Date.now()` internally, even when called from `projectJob`
 *   which had already received a `sharedNow` from `useAdminData`'s
 *   useMemo. Two wall-clock readings inside one `projectJob` call is
 *   a same-job-same-numbers invariant leak (#9) — at day boundaries
 *   `weeksActive` buckets differently than downstream consumers expect.
 *   Same bug class as R13 MED-2 (alertService) + R15 MED-1 (overtime/idle
 *   helpers); this closes it in the velocity layer. Accepts Date OR
 *   number for back-compat with all calling conventions.
 * @returns {Array} annotator velocities
 */
export function computeAnnotatorVelocities(assignments, defaultHoursPerWeek = 20, now = Date.now()) {
  const nowMs = now instanceof Date
    ? now.getTime()
    : (Number.isFinite(now) ? now : Date.now());
  const active = assignments.filter(a => a.status === 'Active');

  return active.map(a => {
    const assignedAt = a.assigned_at ? new Date(a.assigned_at).getTime() : nowMs;
    const weeksActive = Math.max(MIN_WEEKS_ACTIVE_FLOOR, (nowMs - assignedAt) / MS_PER_WEEK);
    const hours = a.hours_logged || 0;
    const annotations = a.annotations_completed || 0;
    const isNew = weeksActive < MIN_WEEKS_FOR_STABLE_DATA;

    // Per-annotator metrics.
    //
    // Phase 23 deep-dive #14 (2026-05-02): cap the linear-extrapolation
    // hoursPerWeek at job capacity for new annotators (weeksActive < 1).
    //
    // Pre-fix: an annotator who logged 24h in their first 2 days got
    // `hours / weeksActive = 24 / (2/7) = 84 hrs/wk` extrapolated forward.
    // Devin Russo on Highway Lane Detection hit the floor case: 24h
    // logged in 0.1 weeks (the MIN_WEEKS_ACTIVE_FLOOR) yielded
    // `24 / 0.143 = 168 hrs/wk` — literally the theoretical maximum
    // (24h × 7d). Real-world this is one full week of working 100% of
    // every hour. Costimpressed forward 12 weeks at his $75/hr rate, this
    // single-annotator inflation pushed Highway's projected cost from
    // ~$40K (the honest $21.8K spent / 54% complete extrapolation) to
    // $200K — a 5× overstatement that propagated into a -150% projected
    // margin, displaced the Cost Overrun action card to "very_low
    // probability of hitting budget", and made every cost-derived KPI
    // on the project dashboard misleading.
    //
    // Fix: for `isNew` annotators (< 1 week active), cap the
    // extrapolation at `defaultHoursPerWeek` (which comes from
    // `job.hours_per_week_max` — the contractually-allowed weekly
    // capacity). For `isNew && hours === 0` we still use defaultHours
    // directly (existing behavior — no data, use capacity estimate).
    // For mature annotators (≥ 1 week active) we trust the historical
    // extrapolation as before — by then the linear projection rests on
    // a denominator wide enough to be stable.
    //
    // The MIN(extrapolated, capacity) shape handles both directions:
    //   - Devin (24h in 2 days):    min(168, 40) = 40  ← capped
    //   - Slow ramp (5h in 2 days): min(17.5, 40) = 17.5 ← real data
    // Mature annotators are unaffected: post-1-week, capacity caps
    // would lose signal so we fall back to the original
    // `hours / weeksActive` extrapolation.
    let hoursPerWeek;
    if (isNew) {
      const extrapolated = hours / weeksActive;
      hoursPerWeek = hours === 0
        ? defaultHoursPerWeek
        : Math.min(extrapolated, defaultHoursPerWeek);
    } else {
      hoursPerWeek = hours / weeksActive;
    }
    const annotationsPerHour = hours > 0 ? annotations / hours : 0;
    const weeklyOutput = hoursPerWeek * annotationsPerHour;

    // Flags
    let flag = null;
    if (isNew && hours === 0) flag = 'new_no_data';
    else if (isNew) flag = 'ramping_up';
    else if (hours > 0 && annotations === 0) flag = 'no_output';

    return {
      id: a.individual_id || a.individual?.id,
      name: a.individual?.name || 'Unknown',
      hoursPerWeek: Math.round(hoursPerWeek * 10) / 10,
      annotationsPerHour: Math.round(annotationsPerHour * 10) / 10,
      // R2 MED-3 audit fix: round to 1 decimal instead of nearest integer.
      // Rounding to an integer destroys variance signal for low-throughput
      // jobs — real velocities {1.4, 1.5, 1.6} becoming {1, 2, 2} inflates
      // stdev from ~0.1 to ~0.577, and teams with real velocity {0.3, 0.4, 0.5}
      // round to {0, 0, 0} and falsely trigger the "zero throughput" branch
      // in projectThroughput. 1-decimal precision preserves the signal
      // while keeping debug dumps human-readable. projectThroughput filters
      // via Number.isFinite (not integer check), so fractional flows through.
      weeklyOutput: Math.round(weeklyOutput * 10) / 10,
      weeksActive: Math.round(weeksActive * 10) / 10,
      hours,
      annotations,
      // R7 MED-1 audit fix: unified via resolveAssignmentRate so the
      // cost coefficient (weightedAvgRate in costProjection.js) uses
      // the SAME rate semantics as projectJob.dollarsSpent. Without
      // this, a `rate=null, confirmed_rate=25` assignment showed up
      // as $0 in the cost projection but $25×hours in dollarsSpent —
      // exact divergence R6 MED-1 aimed to eliminate.
      rate: resolveAssignmentRate(a),
      status: a.status,
      flag,
      isNew,
    };
  });
}

